import {MbscResource} from "@mobiscroll/react";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import React, {useMemo} from "react";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {Box, Button, Dialog, DialogContent, IconButton, Popover, Stack, useTheme} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
    UnassignedType
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AvailabilityPlanner from "crqlar-react-common/dist/components/availability/AvailabilityPlanner";
import ResourceInlineEditDialog from "../../system/services/ResourceInlineEditDialog";
import {usePhoneLayout} from "../../../utils/responsive.util";

interface CalendarResource2Props {
    mbscResource: MbscResource;
    resourcesFilteredByCategory: Resource[];
    childrenResourcesFilteredByCategory: Resource[];
    sector: Sector | undefined;
    viewMode: string | undefined;
    restaurantSectorId: string | undefined;
    onAvailabilitiesChanged: () => void;
}

const CalendarResource = ({
                              mbscResource,
                              resourcesFilteredByCategory,
                              childrenResourcesFilteredByCategory,
                              sector,
                              viewMode,
                              restaurantSectorId,
                              onAvailabilitiesChanged
                          }: CalendarResource2Props) => {
    const { t } = useLocalize()
    const theme = useTheme()
    const isMobile = usePhoneLayout()
    const [actionsAnchorEl, setActionsAnchorEl] = React.useState<HTMLElement | null>(null)
    const [availabilityOpen, setAvailabilityOpen] = React.useState(false)
    const [editDialogOpen, setEditDialogOpen] = React.useState(false)

    const resourceId = mbscResource?.id;

    const rootResource = useMemo(
        () => resourcesFilteredByCategory?.find(r => r.id === resourceId),
        [resourcesFilteredByCategory, resourceId]
    );

    const childResource = useMemo(
        () => childrenResourcesFilteredByCategory?.find(r => r.id === resourceId),
        [childrenResourcesFilteredByCategory, resourceId]
    );

    const editableResource = rootResource ?? childResource;

    const { computedResourceId, name, subtitle, color } = useMemo(() => {
        const computedResourceId = mbscResource.id || '';
        const computedName = mbscResource?.name || '';
        let computedSubtitle = '';
        let computedColor = '';

        if (sector?.sectorType === SectorType.RESTAURANT) {
            const tableResource = childrenResourcesFilteredByCategory?.find(
                r => r.id === resourceId && !!r.parentId
            );
            if (tableResource && viewMode !== 'timeGrid') {
                const capacity = tableResource?.capacity?.toString() || '';
                if (capacity) {
                    computedSubtitle = `${capacity} ${t('guests')}`;
                }
            }
        }

        if (!rootResource?.parentId && rootResource?.meta?.tagColor) {
            computedColor = rootResource.meta.tagColor;
        }

        return {
            computedResourceId: computedResourceId,
            name: computedName,
            subtitle: computedSubtitle,
            color: computedColor,
        };
    }, [
        mbscResource?.id,
        mbscResource?.name,
        sector?.sectorType,
        childrenResourcesFilteredByCategory,
        resourceId,
        viewMode,
        rootResource,
        t,
    ]);

    const isRestaurantResource = rootResource?.sectorIds?.includes(restaurantSectorId as string)
    const isInteractive = !resourceId?.toString().includes(UnassignedType.UNASSIGNED_RESOURCE);
    const isTimelineView = viewMode !== 'timeGrid';
    const isActionsPopoverOpen = Boolean(actionsAnchorEl);
    const isParentRestaurantResource = sector?.sectorType === SectorType.RESTAURANT && !!rootResource && isRestaurantResource && !rootResource?.parentId;
    const canEditResource = !!editableResource;
    const hasAvailability = !!rootResource;

    const getTimelineLeftPadding = () => {
        if (sector?.sectorType === SectorType.RESTAURANT && (!rootResource || isRestaurantResource)) {
            return 2
        }
        return 3
    }

    const openResourceDetails = () => {
        setEditDialogOpen(true)
        setActionsAnchorEl(null)
    }

    const openResourceAvailability = () => {
        setAvailabilityOpen(true)
        setActionsAnchorEl(null)
    }

    const reloadPage = () => {
        window.location.reload()
    }

    const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!isInteractive) return
        setActionsAnchorEl(event.currentTarget)
    }

    const handleTimelineResourceClick = (event: React.MouseEvent<HTMLElement>) => {
        if (event.detail > 0 && !(event.target as HTMLElement).closest('button')) {
            event.currentTarget.blur()
        }
    }

    if (!isMobile) {
        return (
            <>
                {isTimelineView ? (
                    <Box
                        className={`calendar-timeline-resource-cell${isParentRestaurantResource ? ' calendar-timeline-resource-cell--parent' : ''}`}
                        tabIndex={0}
                        onClick={handleTimelineResourceClick}
                        sx={{
                            '--calendar-resource-row-indent': theme.spacing(getTimelineLeftPadding()),
                        }}
                    >
                        <Box className="calendar-timeline-resource-cell__main">
                            <Box
                                className="calendar-timeline-resource-cell__name"
                                sx={{ color: mbscResource?.color }}
                            >
                                {name}
                            </Box>
                            {(canEditResource || hasAvailability) && (
                                <Box className="calendar-timeline-resource-cell__actions">
                                    {canEditResource && (
                                        <IconButton size="small" aria-label={t('Edit')} onClick={openResourceDetails}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    {hasAvailability && (
                                        <IconButton size="small" aria-label={t('Availability')} onClick={openResourceAvailability}>
                                            <EventAvailableIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Box className="calendar-timegrid-resource-cell">
                        <Box
                            className="calendar-timegrid-resource-cell__name"
                            sx={{ color: mbscResource?.color }}
                        >
                            {name}
                        </Box>
                        {(canEditResource || hasAvailability) && (
                            <Box className="calendar-timegrid-resource-cell__actions">
                                {canEditResource && (
                                    <IconButton size="small" aria-label={t('Edit')} onClick={openResourceDetails}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {hasAvailability && (
                                    <IconButton size="small" aria-label={t('Availability')} onClick={openResourceAvailability}>
                                        <EventAvailableIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
                <Dialog
                    open={availabilityOpen}
                    onClose={() => setAvailabilityOpen(false)}
                    fullWidth
                    maxWidth="lg"
                >
                    <DialogContent sx={{ p: 0 }}>
                        <AvailabilityPlanner
                            entityIdFromProps={String(computedResourceId)}
                            withTitle
                            onAvailabilitiesChanged={onAvailabilitiesChanged}
                        />
                    </DialogContent>
                </Dialog>
                {editableResource && (
                    <ResourceInlineEditDialog
                        open={editDialogOpen}
                        resource={editableResource}
                        onClose={() => setEditDialogOpen(false)}
                        onSaved={reloadPage}
                        onDeleted={reloadPage}
                        onAvailabilitiesChanged={onAvailabilitiesChanged}
                    />
                )}
            </>
        );
    }

    return (
        <React.Fragment>
            <Box
                className={`calendar-resource-row${isInteractive ? ' calendar-resource-row--interactive' : ''}`}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    pl: isTimelineView ? getTimelineLeftPadding() : 0,
                    justifyContent: isTimelineView ? 'flex-start' : 'center',
                    position: 'relative',
                }}
            >
                {isTimelineView ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                color: mbscResource?.color,
                                display: 'flex',
                                gap: '4px',
                                alignItems: 'center',
                                textAlign: 'left',
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            {!!color && (
                                <Box
                                    component="span"
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '2px',
                                        backgroundColor: color,
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            {mbscResource?.imgOne}
                            <Box
                                component="span"
                                onClick={handleRowClick}
                                sx={{
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 3,
                                    wordBreak: 'break-word',
                                    cursor: isInteractive ? 'pointer' : 'default',
                                    textAlign: 'left',
                                }}
                            >
                                {name}
                            </Box>
                        </Box>

                        {subtitle && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'normal',
                                    mt: '6px',
                                    textAlign: 'left',
                                    width: '100%',
                                    minWidth: 0,
                                }}
                            >
                                {mbscResource?.imgTwo}
                                <Box
                                    component="span"
                                    sx={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 3,
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {subtitle}
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box
                        component="span"
                        className="calendar-timegrid-resource-cell__name"
                        onClick={handleRowClick}
                        sx={{
                            color: mbscResource?.color,
                            cursor: isInteractive ? 'pointer' : 'default',
                        }}
                    >
                        {name}
                    </Box>
                )}
            </Box>

            <Popover
                open={isActionsPopoverOpen}
                anchorEl={actionsAnchorEl}
                onClose={() => setActionsAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Stack sx={{ p: 1, width: 178, maxWidth: 178 }}>
                    {canEditResource && (
                        <Button
                            startIcon={<EditIcon fontSize="small" />}
                            onClick={openResourceDetails}
                            sx={{
                                justifyContent: 'flex-start',
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                }}
                            >
                                {`${t('Edit')} ${name}`}
                            </Box>
                        </Button>
                    )}
                    {hasAvailability && (
                        <Button
                            startIcon={<EventAvailableIcon fontSize="small" />}
                            onClick={openResourceAvailability}
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            {t('Availability')}
                        </Button>
                    )}
                </Stack>
            </Popover>
            <Dialog
                open={availabilityOpen}
                onClose={() => setAvailabilityOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <DialogContent sx={{ p: 0 }}>
                    <AvailabilityPlanner
                        entityIdFromProps={String(computedResourceId)}
                        withTitle
                        onAvailabilitiesChanged={onAvailabilitiesChanged}
                    />
                </DialogContent>
            </Dialog>
            {editableResource && (
                <ResourceInlineEditDialog
                    open={editDialogOpen}
                    resource={editableResource}
                    onClose={() => setEditDialogOpen(false)}
                    onSaved={reloadPage}
                    onDeleted={reloadPage}
                    onAvailabilitiesChanged={onAvailabilitiesChanged}
                />
            )}
        </React.Fragment>
    );
};

export default CalendarResource;
